import { Box, Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../api/hooks/useAPI";
import { useCheckGoogleRemediationPermissions } from "../../api/hooks/useCheckGoogleRemediationPermissions";
import { useCheckUserPermission } from "../../api/hooks/useCheckUserPermission";
import useColors from "../../api/hooks/useColors";
import {
  controlKeys,
  remediationAuditLogKeys,
  remediationKeys,
} from "../../api/queryKeys";
import SvgToIcon from "../../icons/SvgToIcon";
import { icons } from "../../scenes/stack/environment/icons/iconExport";
import { PLATFORM_USER_ACTIONS } from "../../utils/constants";
import CustomToolTipWithPermission from "../CustomToolTipWithPermission";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import LoadingIcon from "../LoadingIcon";
import { Toast } from "../Toast";

export default function RemovePersonalEmailPermissionsButton({
  controlId = null,
  filesToRestrict = null,
  showToolTip = true,
  disabled = false,
  ...props
}) {
  const colors = useColors();
  const queryClient = useQueryClient();
  const { removePersonalEmailPermissionsFromGoogleFiles, trackEvent } =
    useAPI();
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const { permitted: hasUserPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.RESTRICT_GOOGLE_FILE_ACCESS
  );

  const {
    hasRemediationPermissions,
    isLoading: isCheckingRemediationPermissions,
  } = useCheckGoogleRemediationPermissions();

  const hasAllPermissions = hasUserPermission && hasRemediationPermissions;
  const isButtonDisabled =
    disabled ||
    !hasAllPermissions ||
    isCheckingRemediationPermissions ||
    isRemoving ||
    filesToRestrict.length <= 0;

  const handleRemove = async () => {
    trackEvent(
      "User clicked on 'Remove personal email permissions' button"
    ).then();
    setIsRemoveDialogOpen(true);
  };

  const handleRemoveConfirmButton = async () => {
    trackEvent(
      "User clicked on 'Confirm' button for removing personal email permissions"
    ).then();

    handleCloseDialog();
    setIsRemoving(true);
    Toast.info("Removing personal email permissions...");

    try {
      if (filesToRestrict) {
        const restrictedFilesCountData =
          await removePersonalEmailPermissionsFromGoogleFiles(
            controlId,
            filesToRestrict
          );

        const restrictedFilesCount = restrictedFilesCountData.data;
        const expectedRestrictedFilesCount = filesToRestrict.length;

        if (restrictedFilesCount === expectedRestrictedFilesCount) {
          Toast.success(
            `Successfully removed personal email permissions for ${restrictedFilesCount}/${expectedRestrictedFilesCount} Google files`
          );
        } else if (restrictedFilesCount > 0) {
          Toast.info(
            `Removed personal email permissions for ${restrictedFilesCount}/${expectedRestrictedFilesCount} Google files`
          );
        } else {
          Toast.error(
            "Unable to remove personal email permissions. Please contact support for assistance"
          );
        }

        queryClient.invalidateQueries({
          queryKey: controlKeys.all,
        });
        if (controlId) {
          queryClient.invalidateQueries({
            queryKey: remediationAuditLogKeys.auditLogs(controlId),
          });
        }
        queryClient.invalidateQueries({
          queryKey: remediationKeys.all,
        });
      }
    } catch (error) {
      Toast.error("Failed to remove personal email permissions");
    } finally {
      setIsRemoving(false);
    }
  };

  const handleCloseDialog = () => {
    setIsRemoveDialogOpen(false);
  };

  const getTooltipMessage = () => {
    if (!showToolTip) return "";
    if (!hasUserPermission)
      return 'Your role, "Viewer", does not have permission to perform this action.';
    if (!hasRemediationPermissions) {
      return "Google remediation permissions are required. Please configure them in the Google integration settings.";
    }
    if (isCheckingRemediationPermissions) {
      return "Checking permissions...";
    }
    if (isRemoving) {
      return "Removing personal email permissions...";
    }
    return "Remove personal email permissions for selected files";
  };

  return (
    <>
      <CustomToolTipWithPermission
        title={getTooltipMessage()}
        permission={hasUserPermission}
        displayPermissionDeniedMessage={false}
        placement="top"
      >
        <Box>
          <Button
            sx={{
              color: colors.text_primary,
              backgroundColor: colors.common_white,
              width: "32px",
              height: "32px",
              minWidth: "auto",
              border: "1px solid " + colors.enabled_stroke,
              textTransform: "unset",
              borderRadius: "8px",
              cursor: "pointer",
              ...props.sx,
            }}
            onClick={handleRemove}
            disabled={isButtonDisabled}
          >
            <Box style={{ visibility: isRemoving ? "hidden" : "visible" }}>
              <SvgToIcon
                IconLogo={icons.RestrictFileAccessIcon}
                style={{
                  width: "14px",
                  color: isButtonDisabled
                    ? colors.action_disabled
                    : colors.icon_action_active,
                }}
              />
            </Box>
            {isRemoving && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <LoadingIcon type="circular" size={14} />
              </Box>
            )}
          </Button>
        </Box>
      </CustomToolTipWithPermission>

      <ConfirmationDialog
        openDialog={isRemoveDialogOpen}
        handleDialogClose={handleCloseDialog}
        handleConfirm={handleRemoveConfirmButton}
        dialogTitle="Remove personal email permissions"
        dialogContentFirstLine="Are you sure you want to remove personal email permissions from these files?"
        dialogContentSecondLine="Files will no longer be shared with personal email addresses."
        confirmationButtonText="Remove Permissions"
        destructive={true}
      />
    </>
  );
}
